// Layer 3 (Transformations): Superintendent "MTD costs", "Issues by role"
// and "Production vs plan" tab bodies — map-role-dashboards.md §6b–6d.
// Cost rows and financial totals read the closed-month GmMoney projection.
// MTD cumulative and issues-board stamp derive from the session cursor.

import { gmMoney, gmBudgetTone } from './GmMoney.transform';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { formatDateOffsetFromCursor, issuesBoardTitleStamp, mtdCumulativeFromSeries, mtdPlanRevisionRead, planRevisionOptionLabel, shiftValueAt } from './LandingCursor.transform';
import { feedDesignStph, feedStphAt, p80DesignMicrons } from './LandingSeries.transform';
export { feedDesignStph, feedStphAt } from './LandingSeries.transform';
import { seriesAt } from '@/versions/v1/shared/series.derive';
import { READINGS } from '@/versions/v1/shared/bunker-hill-readings';
import { SMBS_DOSE_PCT } from '@/versions/v1/seed/bunker-hill-issue-spine';

export type CostRow = {
  id: string;
  label: string;
  budget: number;
  actual: number;
  bold?: boolean;
  varianceKind?: 'cost' | 'revenue';
  highlight?: boolean;
};

export function mtdCostBreakdownRows(): CostRow[] {
  return gmMoney().categories.map(r=>({id:r.key,label:r.label,budget:r.total.budget/1000,actual:r.total.actual/1000}));
}

export function mtdCostTotal(rows: readonly CostRow[]): CostRow {
  return {
    id: 'c-total',
    label: 'Total',
    budget: rows.reduce((s, r) => s + r.budget, 0),
    actual: rows.reduce((s, r) => s + r.actual, 0),
    bold: true,
  };
}

export function mtdRevenueEbitdaRows(): CostRow[] {
  const g = gmMoney();
  // Every row compares against the named Budget (the M5 target), the same
  // comparator as the Superintendent tiles, so one figure carries one tone.
  // The panel's BUDGET column header and the panel title already name the
  // comparator, so the row labels do not repeat "· budget".
  return [
    {id:'r-1',label:'Pb/Ag NSR',budget:(g.budgetRevenueByProduct.pb+g.budgetRevenueByProduct.ag)/1000,actual:(g.revenueByProduct.pb+g.revenueByProduct.ag)/1000,varianceKind:'revenue'},
    {id:'r-2',label:'Zn NSR',budget:g.budgetRevenueByProduct.zn/1000,actual:g.revenueByProduct.zn/1000,varianceKind:'revenue'},
    {id:'r-3',label:'Revenue',budget:g.mtd.revenue.target/1000,actual:g.mtd.revenue.actual/1000,bold:true,varianceKind:'revenue'},
    {id:'r-4',label:'Total costs',budget:g.mtd.cost.target/1000,actual:g.mtd.cost.actual/1000,bold:true,varianceKind:'cost'},
    {id:'r-5',label:'Operating EBITDA',budget:g.mtd.ebitda.target/1000,actual:g.mtd.ebitda.actual/1000,bold:true,varianceKind:'revenue',highlight:true},
  ];
}

/** The named financial-budget comparator on the Superintendent cost grid. */
export function costVarianceTone(row: CostRow, kind: 'cost' | 'revenue' = 'cost'): 'ok' | 'warn' | 'bad' {
  return gmBudgetTone(row.actual,row.budget,kind==='cost'?'lower':'higher');
}

export function revenueVarianceTone(row: CostRow): 'ok' | 'warn' | 'bad' {
  return costVarianceTone(row, 'revenue');
}

export function costRowVariance(row: CostRow): number {
  return row.actual - row.budget;
}

export function costTableRows(
  rows: readonly CostRow[],
  opts?: { includeTotal?: boolean; total?: CostRow },
): CostRow[] {
  if (opts?.includeTotal && opts.total) return [...rows, opts.total];
  return [...rows];
}

/** Closed-month cost per short ton, sharing Cost's actual and budget denominators. */
export function mtdCostPerTonne(_state: SessionState): {actualPerTonne:number;budgetPerTonne:number;deltaPerTonne:number} {
  const g = gmMoney();
  return {actualPerTonne:g.actualCostPerSt,budgetPerTonne:g.budgetCostPerSt,deltaPerTonne:g.actualCostPerSt-g.budgetCostPerSt};
}

// ---- Issues by role board (§6c) -----------------------------------------

export type IssueByRoleItem = {
  id: string;
  title: string;
  detail: string;
  priority: 'HIGH' | 'MED';
  status: string;
};

export type IssuesByRoleColumn = {
  id: string;
  roleLabel: string;
  items: readonly IssueByRoleItem[];
};

// Fresh feed at the cursor in stph: the measured `rd-kpi-ore-td` over 24 h, the
// reading every landing's feed figure reads (71.7 stph at the demo cursor), and
// its 75.0 stph design. `feedStphAt` and `feedDesignStph` live in
// `LandingSeries.transform` (the shared landing-series derivation) and are
// re-exported above so one basis serves the Superintendent board and the issue
// list, never a typed rate (#733).

export function issuesByRoleColumns(state: SessionState): IssuesByRoleColumn[] {
  const feed = feedStphAt(state).toFixed(1);
  const pbInZn = shiftValueAt('rs-spec-zn-pb', state.cursor, state)?.toFixed(1) ?? '2.6';
  const p80Design = p80DesignMicrons(state);
  const bearing = seriesAt('rs-bm-brg-temp', state).latest;
  const bearingReading = READINGS.find((row) => row.id === 'rd-bm-brg-temp');
  if (!bearing) throw new Error('Superintendent issue board: missing BM01 bearing reading at cursor');
  if (!bearingReading?.band?.hi || !bearingReading.spec?.hi) {
    throw new Error('Superintendent issue board: missing BM01 warning band or specification limit');
  }
  return [
    {
      id: 'col-cro',
      roleLabel: 'Control Room Operators',
      items: [
        {
          id: 'ibr-1',
          title: 'BM01 bearing TE-622-625 monitoring',
          detail:
            `Hourly temperature log. Currently ${bearing.value.toFixed(1)}°C vs ${bearingReading.band.hi}°C warning / ${bearingReading.spec.hi}°C limit. Alert if rises more than 1°C in any single hour — call Maintenance Supervisor immediately.`,
          priority: 'HIGH',
          status: 'Active',
        },
        {
          id: 'ibr-2',
          title: 'Lime silo LT-500 level checks',
          detail: '30-min checks. At 26%. Switch to manual lime dosing if below 20% before 21:00 delivery.',
          priority: 'HIGH',
          status: 'Active',
        },
        {
          id: 'ibr-3',
          title: `Feed rate hold at ${feed} stph`,
          detail: `Hold ${feed} stph until PSA-001 P80 is at or below ${p80Design} µm design for 4 consecutive hours. Log every 30 min.`,
          priority: 'MED',
          status: 'Active',
        },
      ],
    },
    {
      id: 'col-cmet',
      roleLabel: 'Control Metallurgist',
      items: [
        {
          id: 'ibr-4',
          title: 'Zn recovery compound cause investigation',
          detail: 'Three concurrent causes under investigation — Control Met (Okoye) owns.',
          priority: 'HIGH',
          status: 'Active',
        },
        {
          id: 'ibr-5',
          title: 'CuSO4 new batch preparation',
          detail: 'Target 180 g/L minimum. Titrated 09:45.',
          priority: 'HIGH',
          status: 'In progress',
        },
        {
          id: 'ibr-6',
          title: 'Pb in Zn con SMBS ratio review',
          detail: `At ${pbInZn}% trending to 3.0% limit. Proposes +${SMBS_DOSE_PCT}% SMBS dose. Awaiting Supt approval before action.`,
          priority: 'MED',
          status: 'Draft — not live until approved',
        },
      ],
    },
    {
      id: 'col-maint',
      roleLabel: 'Maintenance Supervisor',
      items: [
        {
          id: 'ibr-7',
          title: 'BM01 vibration check and oil sample tracking',
          detail: 'Vibration check due 14:00 (B. Fletcher). Oil Fe result due 16:00. Go/no-go on controlled inspection.',
          priority: 'HIGH',
          status: 'In progress',
        },
        {
          id: 'ibr-8',
          title: 'AT-150 pH probe calibration support',
          detail: 'AT-150 in MANUAL during calibration. Confirm return to AUTO before end of Day shift.',
          priority: 'HIGH',
          status: 'In progress',
        },
        {
          id: 'ibr-9',
          title: 'Lime delivery coordination',
          detail: 'Clear access route to silo hatch by 18:00. Call supplier before 17:00 to confirm delivery.',
          priority: 'MED',
          status: 'Open',
        },
      ],
    },
  ];
}

export function issuesByRoleBoardMeta(state: SessionState): { total: number; title: string } {
  const columns = issuesByRoleColumns(state);
  const total = columns.reduce((sum, col) => sum + col.items.length, 0);
  return {
    total,
    title: `Active issues by role — top 3 each — ${issuesBoardTitleStamp(state)}`,
  };
}

// ---- Production vs plan (§6d) --------------------------------------------

export type GapClosingOption = {
  id: string;
  option: string;
  potentialGain: string;
  probability: string;
  dependencies: string;
};

export function gapClosingOptions(state: SessionState): GapClosingOption[] {
  const plan = mtdPlanRevisionRead(state);
  // #733: the option raises the measured feed to design once P80 recovers; the
  // rate and the gain read the feed reading, never the retired 57/60 tph text.
  const feed = feedStphAt(state);
  const designFeed = feedDesignStph(state);
  const feedGain = Math.max(0, Math.round((designFeed - feed) * 24));
  return [
    {
      id: 'gc-1',
      option: `Restore feed to ${designFeed.toFixed(1)} stph design when P80 recovers`,
      potentialGain: `${feedGain.toLocaleString('en-US')} dst/day extra`,
      probability: 'High if ore softens',
      dependencies: `Mine planning — softer blend from Stope 9B from ${formatDateOffsetFromCursor(state, 7)}`,
    },
    {
      id: 'gc-2',
      option: 'Zn recovery +1.9 pt via compound cause resolution',
      potentialGain: 'Equivalent to 95 extra dst/day NSR',
      probability: 'Medium — requires 3 sequential fixes',
      dependencies: 'CuSO4 batch today, lime tonight, P80 decision 14:00',
    },
    {
      id: 'gc-3',
      option: 'Reduce planned outages by 1h/day',
      potentialGain: '30 dst/day extra',
      probability: 'Low — PM compliance risk',
      dependencies: 'Maintenance Supt sign-off required',
    },
    {
      id: 'gc-4',
      option: planRevisionOptionLabel(plan.direction),
      potentialGain:
        plan.direction === 'above'
          ? `Run-rate forecast ≈ ${plan.forecastTonnes.toLocaleString()} dst vs ${plan.monthPlan.toLocaleString()} plan`
          : 'Aligns monthly target to current run rate',
      probability: `Recommended if no change by ${formatDateOffsetFromCursor(state, 8)}`,
      dependencies: 'Manager decision required',
    },
  ];
}

/** MTD cumulative tonnes actual vs plan — derived from shift milled series. */
export function mtdCumulativeSeries(state: SessionState): {
  days: string[];
  actual: number[];
  plan: number[];
  label: string;
} {
  return mtdCumulativeFromSeries(state);
}
